import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import { apiKeysApi, usersApi } from '@/api/clients/apiClient'
import { ROLE_RANK, type IssuedApiKey } from '@/api/types'

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatLastUsed(iso: string | null) {
  if (!iso) return 'Never'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return formatDate(iso)
}

export default function ApiKeys() {
  const { orgId, userId, role } = useAuthStore()
  const queryClient = useQueryClient()
  const [isCreating, setIsCreating] = useState(false)
  const [label, setLabel] = useState('')
  const [ownerId, setOwnerId] = useState('') // '' = me
  const [newKey, setNewKey] = useState<IssuedApiKey | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['api-keys', orgId],
    queryFn: () => apiKeysApi.list(orgId!),
    enabled: !!orgId,
  })

  const { data: usersData } = useQuery({
    queryKey: ['users', orgId],
    queryFn: () => usersApi.list(orgId!),
    enabled: !!orgId,
  })

  const users = usersData?.data ?? []
  const userEmailMap = Object.fromEntries(users.map((u) => [u.id, u.email]))
  const userLabel = (id: string | null) =>
    id ? (userEmailMap[id] ?? id.slice(0, 8) + '…') : '—'

  // A key exchanges into a token with its owner's role, so only users ranked at or below
  // me may own one (the backend enforces the same rule).
  const myRank = role ? ROLE_RANK[role] : 0
  const eligibleOwners = users.filter((u) => u.id !== userId && ROLE_RANK[u.role] <= myRank)

  const resetForm = () => {
    setIsCreating(false)
    setLabel('')
    setOwnerId('')
  }

  const issueMutation = useMutation({
    mutationFn: () => apiKeysApi.issue(orgId!, label, ownerId || undefined),
    onSuccess: (issued) => {
      setNewKey(issued)
      resetForm()
      queryClient.invalidateQueries({ queryKey: ['api-keys', orgId] })
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => apiKeysApi.revoke(orgId!, keyId),
    onSuccess: () => {
      setConfirmRevokeId(null)
      queryClient.invalidateQueries({ queryKey: ['api-keys', orgId] })
    },
  })

  const handleCopy = async () => {
    if (!newKey?.key) return
    await navigator.clipboard.writeText(newKey.key)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const keys = data?.data ?? []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="material-icons animate-spin text-primary text-4xl">sync</span>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-light dark:text-text-dark">API Keys</h1>
          <p className="text-sm text-subtle-light dark:text-subtle-dark mt-0.5">
            Keys for programmatic access — stored hashed, shown plaintext once
          </p>
        </div>
        {!isCreating && !newKey && (
          <button
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 text-sm font-medium"
          >
            <span className="material-icons text-base">add</span>
            Issue new key
          </button>
        )}
      </div>

      {/* New key reveal callout */}
      {newKey && (
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg">
          <div className="flex items-start gap-3 mb-3">
            <span className="material-icons text-warning text-xl">warning</span>
            <div>
              <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
                Copy your key now — it will not be shown again
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-0.5">
                Label: <span className="font-mono font-semibold">{newKey.label}</span>
                {newKey.owner_id && (
                  <>
                    {' · '}Owner: <span className="font-semibold">{userLabel(newKey.owner_id)}</span>
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-xs bg-white dark:bg-background-dark border border-border-light dark:border-border-dark rounded px-3 py-2 text-text-light dark:text-text-dark break-all">
              {newKey.key}
            </code>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-3 py-2 bg-warning text-white rounded text-xs font-medium hover:bg-yellow-500 shrink-0"
            >
              <span className="material-icons text-sm">{copied ? 'check' : 'content_copy'}</span>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <button
            onClick={() => {
              setNewKey(null)
              setCopied(false)
            }}
            className="mt-3 text-xs text-subtle-light dark:text-subtle-dark hover:underline"
          >
            I've saved it — dismiss
          </button>
        </div>
      )}

      {/* Issue form */}
      {isCreating && (
        <div className="p-4 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg">
          <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-3">
            Issue new API key
          </h3>
          {issueMutation.isError && (
            <p className="text-sm text-error mb-2">
              {(issueMutation.error as Error)?.message ?? 'Failed to issue key.'}
            </p>
          )}
          <div className="flex items-center gap-2">
            <input
              type="text"
              placeholder="Key label (e.g. CI pipeline)"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
            <select
              aria-label="Owner"
              title="The key acts as this user, with their role"
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              className="px-3 py-2 rounded-lg border border-border-light dark:border-border-dark bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
            >
              <option value="">Owner: me{userId && userEmailMap[userId] ? ` (${userEmailMap[userId]})` : ''}</option>
              {eligibleOwners.map((u) => (
                <option key={u.id} value={u.id}>
                  Owner: {u.email} ({u.role})
                </option>
              ))}
            </select>
            <button
              onClick={() => issueMutation.mutate()}
              disabled={!label.trim() || issueMutation.isPending}
              className="flex items-center gap-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-60 text-sm font-medium"
            >
              {issueMutation.isPending && (
                <span className="material-icons text-base animate-spin">sync</span>
              )}
              Issue
            </button>
            <button
              onClick={resetForm}
              className="px-3 py-2 border border-border-light dark:border-border-dark rounded-lg text-sm text-text-light dark:text-text-dark hover:bg-background-light dark:hover:bg-background-dark"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Keys table */}
      {isError && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-700 dark:text-red-300">Failed to load API keys.</p>
        </div>
      )}

      <div className="bg-surface-light dark:bg-surface-dark rounded-lg shadow-sm border border-border-light dark:border-border-dark overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-light dark:bg-background-dark">
              <tr>
                <th className="px-6 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Label
                </th>
                <th className="px-6 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Owner
                </th>
                <th className="px-6 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Created by
                </th>
                <th className="px-6 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Created
                </th>
                <th className="px-6 py-3 text-left font-medium text-subtle-light dark:text-subtle-dark">
                  Last used
                </th>
                <th className="px-6 py-3 text-right font-medium text-subtle-light dark:text-subtle-dark">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr
                  key={key.id}
                  className="border-t border-border-light dark:border-border-dark hover:bg-background-light dark:hover:bg-background-dark"
                >
                  <td className="px-6 py-3 font-medium text-text-light dark:text-text-dark">
                    <div className="flex items-center gap-2">
                      <span className="material-icons text-subtle-light dark:text-subtle-dark text-base">
                        vpn_key
                      </span>
                      {key.label}
                    </div>
                  </td>
                  <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark truncate max-w-[180px]">
                    {/* Keys issued before owners existed act as their creator. */}
                    {userLabel(key.owner_id ?? key.created_by)}
                  </td>
                  <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark truncate max-w-[180px]">
                    {userLabel(key.created_by)}
                  </td>
                  <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark">
                    {formatDate(key.created_at)}
                  </td>
                  <td className="px-6 py-3 text-subtle-light dark:text-subtle-dark">
                    {formatLastUsed(key.last_used_at)}
                  </td>
                  <td className="px-6 py-3 text-right">
                    {confirmRevokeId === key.id ? (
                      <div className="flex items-center justify-end gap-2">
                        <span className="text-xs text-subtle-light dark:text-subtle-dark">
                          Revoke this key?
                        </span>
                        <button
                          onClick={() => revokeMutation.mutate(key.id)}
                          disabled={revokeMutation.isPending}
                          className="text-xs px-2 py-1 bg-error text-white rounded hover:bg-red-600 disabled:opacity-60"
                        >
                          {revokeMutation.isPending ? '…' : 'Revoke'}
                        </button>
                        <button
                          onClick={() => setConfirmRevokeId(null)}
                          className="text-xs px-2 py-1 border border-border-light dark:border-border-dark rounded hover:bg-background-light dark:hover:bg-background-dark text-text-light dark:text-text-dark"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmRevokeId(key.id)}
                        className="text-subtle-light dark:text-subtle-dark hover:text-error"
                        title="Revoke key"
                      >
                        <span className="material-icons text-lg">delete</span>
                      </button>
                    )}
                  </td>
                </tr>
              ))}

              {keys.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-8 text-center text-subtle-light dark:text-subtle-dark"
                  >
                    No API keys yet.{' '}
                    <button
                      onClick={() => setIsCreating(true)}
                      className="text-primary hover:underline"
                    >
                      Issue one now
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
